import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Box, FundingTrigger, Tooltip, withStyles } from 'vopay';
import { Title, Card, Button, BodyText, RadioCard } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import usePreFundingSettings from '@/hooks/useApi/usePreFundingSettings';
import useFetchDefaultOperationalBankAccount from '@/hooks/useApi/useFetchDefaultOperationalBankAccount';
import PreFundingSettingsSkeleton from './PreFundingSettingsSkeleton';
import styles from './PreFundingSettings.module.scss';

const cn = withStyles(styles);

export default function PreFundingSettings() {
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.PreFundingSettings' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { permissions } = usePermissions();

  const {
    response: { Country },
  } = useClientData();

  const {
    fetching,
    updating,
    fundingTrigger,
    lastSavedTrigger,
    handleSelectTrigger,
    fetchPreFundingSettings,
    updatePreFundingSettings,
  } = usePreFundingSettings();

  const {
    fetching: fetchingBankAccount,
    operationalBankAccount,
    fetchDefaultOperationalBankAccount,
  } = useFetchDefaultOperationalBankAccount();

  const isLoading = fetching || fetchingBankAccount;

  useEffect(() => {
    async function loadPreFundingSettings() {
      try {
        await fetchPreFundingSettings();
      } catch {
        toast.neutral(t('fetchError', { defaultValue: 'Could not load pre-funding settings' }));
      }
    }

    async function loadDefaultBankAccount() {
      try {
        await fetchDefaultOperationalBankAccount({ Currency: Country === 'US' ? 'USD' : 'CAD' });
      } catch {
        toast.neutral(t('fetchBankAccountError', { defaultValue: 'Could not load your operational bank account' }));
      }
    }

    loadPreFundingSettings();
    loadDefaultBankAccount();
  }, [Country]);

  async function updateSettings() {
    try {
      await updatePreFundingSettings({ FundingTrigger: fundingTrigger });

      toast.success(t('saveSuccess', { defaultValue: 'Pre-funding settings saved' }));
    } catch {
      toast.neutral(t('saveError', { defaultValue: 'Could not save pre-funding settings' }));
    }
  }

  const triggerOptions: { value: FundingTrigger; title: string; description: string }[] = [
    {
      value: 'never',
      title: t('Triggers.Never.title', { defaultValue: 'Always from VoPay Balance' }),
      description: t('Triggers.Never.description', {
        defaultValue: 'Every payment is drawn from your VoPay balance.',
      }),
    },
    {
      value: 'insufficient-balance',
      title: t('Triggers.InsufficientBalance.title', {
        defaultValue: 'Fund from bank when the balance is short',
      }),
      description: t('Triggers.InsufficientBalance.description', {
        defaultValue:
          'When your balance does not cover a payment, the full payment amount is pulled from your default operational bank account, and the payment is sent once that funding settles.',
      }),
    },
    {
      value: 'always',
      title: t('Triggers.Always.title', { defaultValue: 'Always fund from bank account' }),
      description: t('Triggers.Always.description', {
        defaultValue:
          'Every payment is backed by a funding transaction for the full payment amount from your default operational bank account. Your VoPay balance is not used.',
      }),
    },
  ];

  return (
    <div className={cn('preFundingSettingsPage')}>
      <Box flx="col" mb="lg" gapSize="8px">
        <Title>{t('title', { defaultValue: 'Pre-Funding Settings' })}</Title>

        <p className={cn('desc')}>
          {t('description', {
            defaultValue:
              'Choose when payments are funded from your bank account. This setting applies to every payment created from your account.',
          })}
        </p>
      </Box>

      <Box flx="col" gapSize="24px">
        <Card style={{ padding: '16px' }}>
          {isLoading ? (
            <PreFundingSettingsSkeleton />
          ) : (
            <Box flx="col" gapSize="16px">
              <Title level="4">{t('fundingBehaviourTitle', { defaultValue: 'Funding behaviour' })}</Title>

              <div className={cn('optionsContainer')}>
                <div className={cn('options')}>
                  {triggerOptions.map((option) => (
                    <RadioCard
                      key={option.value}
                      name="fundingTrigger"
                      value={option.value}
                      checked={fundingTrigger === option.value}
                      onChange={() => handleSelectTrigger(option.value)}
                      disabled={updating || (option.value !== 'never' && !operationalBankAccount)}
                      indicatorPosition="left"
                    >
                      <Box flx="col" gapSize="4px">
                        <BodyText weight="md">{option.title}</BodyText>
                        <BodyText size="sm" className={cn('optionDescription')}>
                          {option.description}
                        </BodyText>
                      </Box>
                    </RadioCard>
                  ))}
                </div>
              </div>
            </Box>
          )}
        </Card>

        {!isLoading && operationalBankAccount && (
          <Card style={{ padding: '16px' }}>
            <Box flx="col" gapSize="16px">
              <Box ai="center" gapSize="8px">
                <Title level="4">{t('FundedFrom.label', { defaultValue: 'Funded from' })}</Title>

                <Tooltip
                  position="right"
                  icon="info"
                  information={
                    <BodyText>
                      <Box gapSize="8px" flx="col">
                        <span>
                          {t('FundedFrom.tooltip', {
                            defaultValue:
                              'Payments are funded from this account. To change the payment method it uses, go to the Operational Account page.',
                          })}
                        </span>

                        <Link to="/operational-accounts">
                          {t('FundedFrom.tooltipLink', { defaultValue: 'Operational Account' })}
                        </Link>
                      </Box>
                    </BodyText>
                  }
                />
              </Box>

              <Box flx="col" gapSize="8px">
                <div className={cn('accountContainer')}>
                  {operationalBankAccount.InstitutionName} {operationalBankAccount.AccountNumber}
                </div>

                <div className={cn('description')}>
                  <Trans
                    t={t}
                    i18nKey="footerNote"
                    defaults="When a payment is funded from your bank account, the funding transaction is always for the full payment amount — not the difference. The payment stays in <bold>waiting</bold> until the funding settles."
                    components={{ bold: <span className={cn('fontWeight600')} /> }}
                  />
                </div>
              </Box>
            </Box>
          </Card>
        )}

        {!isLoading && permissions.AddSettings && (
          <Box jc="fe">
            <Button
              colorScheme="primary"
              disabled={updating || fundingTrigger === lastSavedTrigger}
              onClick={updateSettings}
            >
              {tGlobal('Buttons.save')}
            </Button>
          </Box>
        )}
      </Box>
    </div>
  );
}
